import * as React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Grid, IconButton, Tooltip, Typography } from "@mui/material";
import ModalHeader from "./ModalHeader";
import ModalFooter from "./ModalFooter";
import { useState, useMemo, useEffect } from "react";
import WalletIcon from "@mui/icons-material/Wallet";
import Loader from "../component/loading-screen/Loader";
import { postJsonData } from "../network/ApiController";
import ApiEndpoints from "../network/ApiEndPoints";
import { apiErrorToast, okSuccessToast } from "../utils/ToastUtil";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { Controller, useForm, useWatch } from "react-hook-form";
import RHFTextField from "../component/RHFTextField";
import { PATTERNS } from "../utils/ValidationUtil";
import ViewModal from "./ViewModal";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflowY: "auto",
};

const SUPPORTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/jpg"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const AddBalanceViaPG = ({ refresh }) => {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(false);
  const [aadhaarAvailable, setAadhaarAvailable] = useState(false);
  const [data, setData] = useState({});
  const [otp, setOtp] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [customerCreated, setCustomerCreated] = useState(false);
  const [response, setResponse] = useState(false);

const [customerFound, setCustomerFound] = useState(false);
  const [otpFieldVisible, setOtpFieldVisible] = useState(false);

  
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    reset({
              ...defaultValues,
              cust_phone: "",
            });
    setData({});
    setAadhaarAvailable(false);
    setShowAddCustomer(false);
    setCustomerCreated(false);
    setCustomerFound(false);
    setOtpFieldVisible(false);
    setOpen(false);
    setOtp(false);
  };

  const defaultValues = useMemo(
    () => ({
      cust_email: "",
      cust_name: "",
      cust_phone: "",
      amount: "",
      aadhaar_no: "",
      aadhaar_front: null,
      aadhaar_back: null,
      aadhaar_withCustomer: null,
      pan_or_cc_front: null,
      customer_pic: null,
      consent: false,
      otp: "",
    }),
    []
  );

  const fields = [
    { label: "Aadhaar Front", name: "aadhaar_front" },
    { label: "Aadhaar Back", name: "aadhaar_back" },
    { label: "PAN or CC Front", name: "pan_or_cc_front" },
    { label: "Customer Pic", name: "customer_pic" },
  ];

  const schema = yup.object().shape({
    cust_email: yup.string().required("Customer email required").email("Invalid email format"),
    cust_name: yup.string().required("Customer name required"),
    cust_phone: yup
      .string()
      .required("Customer phone required")
      .matches(PATTERNS.MOBILE, "Invalid mobile number"),
    aadhaar_no: showAddCustomer && !aadhaarAvailable 
      ? yup.string().required("Aadhaar number required").matches(/^\d{12}$/, "Aadhaar must be 12 digits")
      : yup.string(),
    amount: response 
      ? yup
          .number()
          .required("Transfer amount required")
          .min(100, "Minimum amount is ₹100")
          .max(500000, "Maximum amount is ₹500,000")
          .typeError("Amount must be a number")
      : yup.mixed().notRequired(),
    otp: otp 
      ? yup.string().required("OTP required").matches(/^\d{6}$/, "OTP must be 6 digits")
      : yup.mixed().notRequired(),
    ...(showAddCustomer && !aadhaarAvailable ? {
      aadhaar_front: yup
        .mixed()
        .required("Aadhaar front image is required")
        .test(
          "fileSize",
          "File too large (max 5MB)",
          value => !value || (value && value.size <= MAX_FILE_SIZE)
        )
        .test(
          "fileType",
          "Unsupported file format (JPEG/PNG only)",
          value => !value || (value && SUPPORTED_IMAGE_TYPES.includes(value.type))
        ),
      aadhaar_back: yup
        .mixed()
        .required("Aadhaar back image is required")
        .test(
          "fileSize",
          "File too large (max 5MB)",
          value => !value || (value && value.size <= MAX_FILE_SIZE)
        )
        .test(
          "fileType",
          "Unsupported file format (JPEG/PNG only)",
          value => !value || (value && SUPPORTED_IMAGE_TYPES.includes(value.type))
        ),
      pan_or_cc_front: yup
        .mixed()
        .required("PAN or CC front image is required")
        .test(
          "fileSize",
          "File too large (max 5MB)",
          value => !value || (value && value.size <= MAX_FILE_SIZE)
        )
        .test(
          "fileType",
          "Unsupported file format (JPEG/PNG only)",
          value => !value || (value && SUPPORTED_IMAGE_TYPES.includes(value.type))
        ),
      customer_pic: yup
        .mixed()
        .required("Customer picture is required")
        .test(
          "fileSize",
          "File too large (max 5MB)",
          value => !value || (value && value.size <= MAX_FILE_SIZE)
        )
        .test(
          "fileType",
          "Unsupported file format (JPEG/PNG only)",
          value => !value || (value && SUPPORTED_IMAGE_TYPES.includes(value.type))
        ),
    } : {})
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    mode: "onChange", // Validate on change for better UX
  });

  const cust_phone = useWatch({ control, name: "cust_phone" });

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (cust_phone?.length !== 10) return;
      
      postJsonData(
        ApiEndpoints.GET_CUSTOMERSINGAL,
        { mobile: cust_phone },
        setRequest,
        (res) => {
          const customer = res?.data?.data?.[0];
             
          if (customer) {
            setOtpFieldVisible(false);
            setData(customer);
            setAadhaarAvailable(true);
            setShowAddCustomer(true);
            setCustomerCreated(true);
            setOtp(false);
            setResponse(true);
            setValue("cust_name", customer?.name || "");
            setValue("cust_email", customer?.email || "");
            setValue("aadhaar_no", customer?.aadhaar || "");
            setValue("aadhaar_front", customer?.aadhaar_front || "");
            setValue("aadhaar_back", customer?.aadhaar_back || "");
            setValue("pan_or_cc_front", customer?.pan_or_cc_front || "");
            setValue("customer_pic", customer?.customer_pic || "");
          } else {
            setOtp(true);
            setData({});
            setAadhaarAvailable(false);
            setShowAddCustomer(true);
            setCustomerCreated(false);
            setResponse(false);
            reset({
              ...defaultValues,
              cust_phone: cust_phone,
            });
          }
          trigger(); // Re-trigger validation after state changes
        },
        (err) => {
          if (err?.response?.status === 404) {
            setResponse(false);
            setData({});
            setAadhaarAvailable(false);
            setShowAddCustomer(true);
            setCustomerCreated(false);
            reset({
              ...defaultValues,
              cust_phone: "",
            });
          } else {
            apiErrorToast(err);
            setResponse(false);
            setData({});
            setAadhaarAvailable(false);
            setShowAddCustomer(false);
            setCustomerCreated(false);
            reset({
              ...defaultValues,
              cust_phone: cust_phone,
            });
          }
          trigger(); // Re-trigger validation after state changes
        }
      );
    };

    const timer = setTimeout(() => {
      if (cust_phone?.length === 10) {
        fetchCustomerDetails();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [cust_phone, reset, setValue, defaultValues]);



  const handleAddCustomer = async (formData) => {
    const fd = new FormData();
    fd.append("name", formData.cust_name);
    fd.append("email", formData.cust_email);
    if (otpFieldVisible) fd.append("otp", formData.otp);
    fd.append("mobile", formData.cust_phone);
    fd.append("otp", formData.otp);
    fd.append("aadhaar", formData.aadhaar_no);
    if (formData.aadhaar_front)
      fd.append("aadhaar_front", formData.aadhaar_front);
    if (formData.aadhaar_back) fd.append("aadhaar_back", formData.aadhaar_back);
    if (formData.aadhaar_withCustomer)
      fd.append("aadhaar_withCustomer", formData.aadhaar_withCustomer);
    if (formData.pan_or_cc_front)
      fd.append("pan_or_cc_front", formData.pan_or_cc_front);
    if (formData.customer_pic) fd.append("customer_pic", formData.customer_pic);

    postJsonData(
      ApiEndpoints.ADD_CUSTOMER,
      fd,
      setRequest,
      (res) => {
        okSuccessToast(res.data.message);
        setCustomerCreated(true);
        setShowAddCustomer(false);
        handleClose();
      },
      apiErrorToast,
      true
    );
  };

  const createOrder = (fData) => {
    if (response) {
      postJsonData(
        ApiEndpoints.CREATE_ORDER,
        fData,
        setRequest,
        (res) => {
          const data = res.data.data;
          const newData = {
            ...data,
          };
          postRedirect("https://order.usequickpay.com/order/pay", newData);
        },
        (err) => {
          apiErrorToast(err);
        }
      );
    } else {
      handleAddCustomer(fData);
    }
  };

  return (
    <>
      <div className="hover-zoom" onClick={handleOpen}>
        <IconButton sx={{ display: "contents", color: "#fff" }}>
          <Tooltip title="Add Money" placement="left">
            <div className="d-flex">
              <WalletIcon sx={{ color: "#D53E3E" }} />
              <Typography sx={{ fontWeight: "bold" }}>+</Typography>
            </div>
          </Tooltip>
        </IconButton>
      </div>

      <Modal open={open} onClose={handleClose}>
        <Box sx={style} className="sm_modal">
          <Loader loading={request} />
          <ModalHeader title="Add Balance" handleClose={handleClose} />

          <Box
            component="form"
            id="money_transfer"
            onSubmit={handleSubmit(createOrder)}
            noValidate // Prevent default HTML5 validation
            autoComplete="off"
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <RHFTextField
                  label="Customer Mobile *"
                  name="cust_phone"
                  control={control}
                  errors={errors}
                  inputProps={{
                    maxLength: 10,
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  }}
                />
              </Grid>

              {(showAddCustomer || !aadhaarAvailable) && (
                <>
                  <Grid item md={6} xs={12}>
                    <RHFTextField
                      label="Customer Name *"
                      name="cust_name"
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                 {/* {showAddCustomer && !aadhaarAvailable && (<> */}
                  <Grid item md={6} xs={12}>
                    <RHFTextField
                      label="Customer Email *"
                      name="cust_email"
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                
                  <Grid item xs={12}>
                    <RHFTextField
                      label="Aadhaar Number *"
                      name="aadhaar_no"
                      control={control}
                      errors={errors}
                      inputProps={{
                        maxLength: 12,
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                    />
                  </Grid>
                  {/* </>)} */}
                  
                  {otp &&    
                    <Grid item xs={12}>
                      <RHFTextField
                        label="OTP *"
                        name="otp"
                        control={control}
                        errors={errors}
                        inputProps={{
                          maxLength: 6,
                          inputMode: 'numeric',
                          pattern: '[0-9]*'
                        }}
                      />
                    </Grid>
                  }

                  {/* Image upload fields - shown only for new customers */}
                  {showAddCustomer && !aadhaarAvailable && (
                    <>
                      {fields.map((field, index) => (
                        <Grid key={index} item md={6} xs={12}>
                          <Controller
                            name={field.name}
                            control={control}
                            render={({ field: controllerField }) => (
                              <div>
                                <label htmlFor={field.name} style={{ fontWeight: 500 }}>
                                  {field.label} *
                                </label>
                                <input
                                  id={field.name}
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      controllerField.onChange(file);
                                    }
                                  }}
                                  style={{ 
                                    display: "block", 
                                    marginTop: 8,
                                    width: '100%',
                                    padding: '8px',
                                    border: errors[field.name] ? '1px solid red' : '1px solid #ccc',
                                    borderRadius: '4px'
                                  }}
                                />
                                {errors[field.name] && (
                                  <span style={{ 
                                    color: "red", 
                                    fontSize: "12px",
                                    display: 'block',
                                    marginTop: '4px'
                                  }}>
                                    {errors[field.name]?.message}
                                  </span>
                                )}
                                <small style={{ display: 'block', color: '#666', marginTop: '4px' }}>
                                  Max 5MB, JPEG/PNG only
                                </small>
                              </div>
                            )}
                          />
                        </Grid>
                      ))}
                    </>
                  )}

                  {/* Show existing images if available */}
                  {aadhaarAvailable && 
                    fields.map((field, index) =>
                      data?.[field.name] ? (
                        <Grid key={index} item md={6} xs={12}>
                          <label htmlFor={field.name} style={{ fontWeight: 500 }}>
                            {field.label}
                          </label>
                          <ViewModal image={data[field.name]} refresh={refresh} />
                        </Grid>
                      ) : null
                    )}

                  {response && (
                    <Grid item xs={12}>
                      <RHFTextField
                        label="Amount *"
                        name="amount"
                        control={control}
                        errors={errors}
                        inputProps={{
                          inputMode: 'numeric',
                          pattern: '[0-9]*'
                        }}
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Box>
          <ModalFooter
            form="money_transfer"
            request={request}
            btn="Submit"
            disabled={!isValid} // Disable button when form is invalid
          />
        </Box>
      </Modal>
    </>
  );
}
export default AddBalanceViaPG;

const postRedirect = (url, data) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = url;
  form.target = "_blank";

  Object.keys(data).forEach((key) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = data[key];
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};
